import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import QuickActionButton from '../../components/ui/QuickActionButton';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import BrandCategoryPanel from './components/BrandCategoryPanel';
import VisualRepository from './components/VisualRepository';
import UploadModal from './components/UploadModal';
import BrandFormModal from './components/BrandFormModal';
import CategoryFormModal from './components/CategoryFormModal';
import KVReplacementModal from './components/KVReplacementModal';

const KeyVisualManagement = () => {
  const navigate = useNavigate();
  
  // State management
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [keyVisuals, setKeyVisuals] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  
  // Archive functionality states
  const [currentTab, setCurrentTab] = useState('active'); // 'active' or 'archived'
  const [currentUserRole, setCurrentUserRole] = useState('admin'); // Simulate user role (admin, manager, user)
  const [deletionModalOpen, setDeletionModalOpen] = useState(false);
  const [selectedVisualForDeletion, setSelectedVisualForDeletion] = useState(null);
  
  // KV ID reservation system - stores deleted KV IDs permanently
  const [reservedKvIds, setReservedKvIds] = useState(new Set());

  // New state for KV replacement
  const [replacementModalOpen, setReplacementModalOpen] = useState(false);
  const [selectedVisualForReplacement, setSelectedVisualForReplacement] = useState(null);
  const [dependencyCheckResult, setDependencyCheckResult] = useState(null);

  // Modal states
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [isBrandModalOpen, setIsBrandModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  
  // Form modes
  const [brandFormMode, setBrandFormMode] = useState('create');
  const [categoryFormMode, setCategoryFormMode] = useState('create');
  const [editingBrand, setEditingBrand] = useState(null);
  const [editingCategory, setEditingCategory] = useState(null);

  // Mock data with KV_Status field
  const mockBrands = [
    { id: 'BRD001', name: 'Lily', code: 'LIS', description: 'Premium beauty brand' },
    { id: 'BRD002', name: 'Cavotin', code: 'CAV', description: 'Professional hair care' },
    { id: 'BRD003', name: 'Orix', code: 'ORI', description: 'Home and fabric care solutions' },
    { id: 'BRD004', name: 'Viva', code: 'VIV', description: 'Modern lifestyle brand' },
    { id: 'BRD005', name: 'Sparkle', code: 'SPK', description: 'Youth-focused cosmetics' },
    { id: 'BRD006', name: 'Elite', code: 'ELT', description: 'Luxury personal care' },
  ];

  const mockCategories = [
    { id: 'CAT001', name: 'Color Cosmetics', code: 'CC', description: 'Makeup and color products' },
    { id: 'CAT002', name: 'Skincare', code: 'SC', description: 'Skin care and treatment products' },
    { id: 'CAT003', name: 'Haircare', code: 'HC', description: 'Hair care and styling products' },
    { id: 'CAT004', name: 'Home & Fabric Care', code: 'HFC', description: 'Household cleaning products' },
    { id: 'CAT005', name: 'Personal Care', code: 'PC', description: 'Personal hygiene and care products' },
    { id: 'CAT006', name: 'Wellness', code: 'WL', description: 'Health and wellness products' },
  ];

  const mockKeyVisuals = [
    // ACTIVE KEY VISUALS - Current campaigns and content
    {
      id: 'KV001',
      kvId: 'LIS-SC-001',
      brandId: 'BRD001',
      categoryId: 'CAT002',
      title: 'Lily Skincare Summer Glow Campaign 2024',
      fileName: 'lily_skincare_summer_glow.jpg',
      fileType: 'image',
      fileSize: '2.5MB',
      dimensions: '20x12 ft',
      brandingType: 'Billboard',
      uploadDate: '2024-09-15',
      usageCount: 5,
      usedIn: ['Billboard - Dhanmondi', 'Billboard - Gulshan', 'In-Store Static - Chittagong', 'Window Display - Sylhet', 'LED Screen - Rajshahi'],
      kvStatus: 'Active',
      archivedDate: null,
      archivedBy: null,
      thumbnailUrl: 'https://images.unsplash.com/photo-1596755389378-c31d21fd1273?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: null,
      durationDisplay: null
    },
    {
      id: 'KV002',
      kvId: 'CAV-HC-001',
      brandId: 'BRD002',
      categoryId: 'CAT003',
      title: 'Cavotin Professional Hair Transformation',
      fileName: 'cavotin_professional_transform.mp4',
      fileType: 'video',
      fileSize: '15.2MB',
      dimensions: '16x9 ft',
      brandingType: 'LED Screen',
      uploadDate: '2024-09-20',
      usageCount: 3,
      usedIn: ['LED Screen - Shopping Mall', 'LED Screen - City Center', 'LED Screen - Trade Center'],
      kvStatus: 'Active',
      archivedDate: null,
      archivedBy: null,
      thumbnailUrl: 'https://images.unsplash.com/photo-1560066984-138dadb4c035?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: 45,
      durationDisplay: '0:45'
    },
    {
      id: 'KV003',
      kvId: 'ORI-HFC-001',
      brandId: 'BRD003',
      categoryId: 'CAT004',
      title: 'Orix Home Care Complete Solutions',
      fileName: 'orix_homecare_complete.jpg',
      fileType: 'image',
      fileSize: '3.1MB',
      dimensions: '15x10 ft',
      brandingType: 'Gondola Branding',
      uploadDate: '2024-09-25',
      usageCount: 6,
      usedIn: ['Gondola - Store 1', 'Gondola - Store 2', 'In-Store Static - Store 3', 'Window Display - Store 4', 'FSU - Store 5', 'FSU - Store 6'],
      kvStatus: 'Active',
      archivedDate: null,
      archivedBy: null,
      thumbnailUrl: 'https://images.unsplash.com/photo-1585515656853-c6b58a6ca2a5?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: null,
      durationDisplay: null
    },
    {
      id: 'KV007',
      kvId: 'VIV-PC-001',
      brandId: 'BRD004',
      categoryId: 'CAT005',
      title: 'Viva Personal Care Modern Lifestyle',
      fileName: 'viva_modern_lifestyle.mp4',
      fileType: 'video',
      fileSize: '18.7MB',
      dimensions: '18x12 ft',
      brandingType: 'LED Screen',
      uploadDate: '2024-10-01',
      usageCount: 4,
      usedIn: ['LED Screen - Mall 1', 'LED Screen - Mall 2', 'Billboard - Highway', 'In-Store Static - Premium Store'],
      kvStatus: 'Active',
      archivedDate: null,
      archivedBy: null,
      thumbnailUrl: 'https://images.unsplash.com/photo-1522335789203-aabd1fc54bc9?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: 30,
      durationDisplay: '0:30'
    },
    {
      id: 'KV008',
      kvId: 'SPK-CC-001',
      brandId: 'BRD005',
      categoryId: 'CAT001',
      title: 'Sparkle Youth Color Collection 2024',
      fileName: 'sparkle_youth_color_2024.jpg',
      fileType: 'image',
      fileSize: '2.8MB',
      dimensions: '14x8 ft',
      brandingType: 'Window Display',
      uploadDate: '2024-09-28',
      usageCount: 7,
      usedIn: ['Window Display - Fashion Store 1', 'Window Display - Fashion Store 2', 'Window Display - Beauty Counter 1', 'In-Store Static - Young Adult Section', 'FSU - College Campus 1', 'FSU - College Campus 2', 'Gondola - Teen Section'],
      kvStatus: 'Active',
      archivedDate: null,
      archivedBy: null,
      thumbnailUrl: 'https://images.unsplash.com/photo-1616683693504-3ea7e9ad6fec?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: null,
      durationDisplay: null
    },
    {
      id: 'KV009',
      kvId: 'ELT-WL-001',
      brandId: 'BRD006',
      categoryId: 'CAT006',
      title: 'Elite Wellness Premium Experience',
      fileName: 'elite_wellness_premium.jpg',
      fileType: 'image',
      fileSize: '4.2MB',
      dimensions: '22x14 ft',
      brandingType: 'Billboard',
      uploadDate: '2024-10-02',
      usageCount: 2,
      usedIn: ['Billboard - Premium Location 1', 'Billboard - Premium Location 2'],
      kvStatus: 'Active',
      archivedDate: null,
      archivedBy: null,
      thumbnailUrl: 'https://images.unsplash.com/photo-1540555700478-4be289fbecef?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: null,
      durationDisplay: null
    },
    {
      id: 'KV010',
      kvId: 'LIS-CC-002',
      brandId: 'BRD001',
      categoryId: 'CAT001',
      title: 'Lily Color Cosmetics Autumn Collection',
      fileName: 'lily_autumn_colors.mp4',
      fileType: 'video',
      fileSize: '12.5MB',
      dimensions: '16x9 ft',
      brandingType: 'LED Screen',
      uploadDate: '2024-09-30',
      usageCount: 3,
      usedIn: ['LED Screen - Beauty Mall 1', 'LED Screen - Beauty Mall 2', 'In-Store Static - Cosmetic Counter'],
      kvStatus: 'Active',
      archivedDate: null,
      archivedBy: null,
      thumbnailUrl: 'https://images.unsplash.com/photo-1586495777744-4413f21062fa?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: 25,
      durationDisplay: '0:25'
    },
    {
      id: 'KV011',
      kvId: 'CAV-HC-003',
      brandId: 'BRD002',
      categoryId: 'CAT003',
      title: 'Cavotin Hair Repair Intensive Treatment',
      fileName: 'cavotin_hair_repair_intensive.jpg',
      fileType: 'image',
      fileSize: '3.5MB',
      dimensions: '12x8 ft',
      brandingType: 'In-Store Static',
      uploadDate: '2024-10-01',
      usageCount: 5,
      usedIn: ['In-Store Static - Salon 1', 'In-Store Static - Salon 2', 'Window Display - Beauty Store 1', 'FSU - Hair Care Section 1', 'FSU - Hair Care Section 2'],
      kvStatus: 'Active',
      archivedDate: null,
      archivedBy: null,
      thumbnailUrl: 'https://images.unsplash.com/photo-1559599101-f09722fb4948?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: null,
      durationDisplay: null
    },
    {
      id: 'KV012',
      kvId: 'ORI-HFC-002',
      brandId: 'BRD003',
      categoryId: 'CAT004',
      title: 'Orix Fabric Care Advanced Formula',
      fileName: 'orix_fabric_advanced.jpg',
      fileType: 'image',
      fileSize: '2.1MB',
      dimensions: '10x6 ft',
      brandingType: 'FSU',
      uploadDate: '2024-09-27',
      usageCount: 4,
      usedIn: ['FSU - Supermarket 1', 'FSU - Supermarket 2', 'Gondola - Home Section 1', 'Gondola - Home Section 2'],
      kvStatus: 'Active',
      archivedDate: null,
      archivedBy: null,
      thumbnailUrl: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: null,
      durationDisplay: null
    },
    {
      id: 'KV013',
      kvId: 'VIV-SC-001',
      brandId: 'BRD004',
      categoryId: 'CAT002',
      title: 'Viva Skincare Natural Glow Series',
      fileName: 'viva_natural_glow_series.mp4',
      fileType: 'video',
      fileSize: '14.8MB',
      dimensions: '15x10 ft',
      brandingType: 'LED Screen',
      uploadDate: '2024-10-03',
      usageCount: 2,
      usedIn: ['LED Screen - Beauty Center 1', 'Window Display - Skincare Store 1'],
      kvStatus: 'Active',
      archivedDate: null,
      archivedBy: null,
      thumbnailUrl: 'https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: 35,
      durationDisplay: '0:35'
    },

    // ARCHIVED KEY VISUALS - Previous campaigns that have been replaced or discontinued
    {
      id: 'KV004',
      kvId: 'LIS-CC-001',
      brandId: 'BRD001',
      categoryId: 'CAT001',
      title: 'Lily Color Cosmetics Winter Holiday Collection',
      fileName: 'lily_color_winter_holiday.jpg',
      fileType: 'image',
      fileSize: '1.8MB',
      dimensions: '18x10 ft',
      brandingType: 'Billboard',
      uploadDate: '2024-08-10',
      usageCount: 2,
      usedIn: ['Billboard - Uttara', 'Window Display - Store 5'],
      kvStatus: 'Archived',
      archivedDate: '2024-09-30',
      archivedBy: 'Sarah Johnson',
      thumbnailUrl: 'https://images.unsplash.com/photo-1512496015851-a90fb38ba796?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: null,
      durationDisplay: null
    },
    {
      id: 'KV005',
      kvId: 'CAV-HC-002',
      brandId: 'BRD002',
      categoryId: 'CAT003',
      title: 'Cavotin Professional Hair Series - Previous Version',
      fileName: 'cavotin_professional_old.mp4',
      fileType: 'video',
      fileSize: '12.1MB',
      dimensions: '16x9 ft',
      brandingType: 'LED Screen',
      uploadDate: '2024-07-15',
      usageCount: 1,
      usedIn: ['LED Screen - Old Campaign'],
      kvStatus: 'Archived',
      archivedDate: '2024-09-28',
      archivedBy: 'Mike Chen',
      thumbnailUrl: 'https://images.unsplash.com/photo-1634449571010-02389ed0f9b0?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: 40,
      durationDisplay: '0:40'
    },
    {
      id: 'KV006',
      kvId: 'ORI-HFC-003',
      brandId: 'BRD003',
      categoryId: 'CAT004',
      title: 'Orix Home Care Spring Clean Campaign',
      fileName: 'orix_spring_clean_2024.jpg',
      fileType: 'image',
      fileSize: '2.7MB',
      dimensions: '16x10 ft',
      brandingType: 'Billboard',
      uploadDate: '2024-06-20',
      usageCount: 3,
      usedIn: ['Billboard - Spring Campaign 1', 'Billboard - Spring Campaign 2', 'In-Store Static - Seasonal Display'],
      kvStatus: 'Archived',
      archivedDate: '2024-09-15',
      archivedBy: 'Lisa Wang',
      thumbnailUrl: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: null,
      durationDisplay: null
    },
    {
      id: 'KV014',
      kvId: 'SPK-CC-002',
      brandId: 'BRD005',
      categoryId: 'CAT001',
      title: 'Sparkle Summer Vibrant Colors 2024',
      fileName: 'sparkle_summer_vibrant.jpg',
      fileType: 'image',
      fileSize: '2.3MB',
      dimensions: '12x8 ft',
      brandingType: 'Window Display',
      uploadDate: '2024-05-15',
      usageCount: 4,
      usedIn: ['Window Display - Summer Store 1', 'Window Display - Summer Store 2', 'FSU - Beach Location', 'Gondola - Summer Section'],
      kvStatus: 'Archived',
      archivedDate: '2024-09-25',
      archivedBy: 'David Rodriguez',
      thumbnailUrl: 'https://images.unsplash.com/photo-1567401893414-76b7b1e5a7a5?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: null,
      durationDisplay: null
    },
    {
      id: 'KV015',
      kvId: 'ELT-PC-001',
      brandId: 'BRD006',
      categoryId: 'CAT005',
      title: 'Elite Personal Care Luxury Line - Legacy',
      fileName: 'elite_luxury_legacy.mp4',
      fileType: 'video',
      fileSize: '16.9MB',
      dimensions: '20x12 ft',
      brandingType: 'LED Screen',
      uploadDate: '2024-07-01',
      usageCount: 2,
      usedIn: ['LED Screen - Luxury Mall 1', 'Billboard - Premium District'],
      kvStatus: 'Archived',
      archivedDate: '2024-10-01',
      archivedBy: 'Amanda Foster',
      thumbnailUrl: 'https://images.unsplash.com/photo-1571781926291-c477ebfd024b?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: 50,
      durationDisplay: '0:50'
    },
    {
      id: 'KV016',
      kvId: 'VIV-WL-001',
      brandId: 'BRD004',
      categoryId: 'CAT006',
      title: 'Viva Wellness Mind Body Balance - OLD',
      fileName: 'viva_wellness_old_campaign.jpg',
      fileType: 'image',
      fileSize: '3.6MB',
      dimensions: '14x10 ft',
      brandingType: 'In-Store Static',
      uploadDate: '2024-06-10',
      usageCount: 3,
      usedIn: ['In-Store Static - Wellness Center 1', 'Window Display - Health Store', 'FSU - Wellness Section'],
      kvStatus: 'Archived',
      archivedDate: '2024-09-20',
      archivedBy: 'Robert Kim',
      thumbnailUrl: 'https://images.unsplash.com/photo-1506629905607-d6968d6ac4b3?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: null,
      durationDisplay: null
    },
    {
      id: 'KV017',
      kvId: 'LIS-SC-002',
      brandId: 'BRD001',
      categoryId: 'CAT002',
      title: 'Lily Skincare Anti-Aging Spring Campaign',
      fileName: 'lily_anti_aging_spring.jpg',
      fileType: 'image',
      fileSize: '2.9MB',
      dimensions: '16x12 ft',
      brandingType: 'Billboard',
      uploadDate: '2024-04-22',
      usageCount: 5,
      usedIn: ['Billboard - Main Highway 1', 'Billboard - Main Highway 2', 'Window Display - Premium Store 1', 'In-Store Static - Skincare Section 1', 'In-Store Static - Skincare Section 2'],
      kvStatus: 'Archived',
      archivedDate: '2024-09-12',
      archivedBy: 'Jennifer Lee',
      thumbnailUrl: 'https://images.unsplash.com/photo-1556228720-195a672e8a03?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: null,
      durationDisplay: null
    },
    {
      id: 'KV018',
      kvId: 'CAV-PC-001',
      brandId: 'BRD002',
      categoryId: 'CAT005',
      title: 'Cavotin Personal Care Complete Range - Discontinued',
      fileName: 'cavotin_complete_range_old.mp4',
      fileType: 'video',
      fileSize: '11.3MB',
      dimensions: '14x9 ft',
      brandingType: 'LED Screen',
      uploadDate: '2024-05-30',
      usageCount: 2,
      usedIn: ['LED Screen - Personal Care Mall', 'In-Store Static - Care Section'],
      kvStatus: 'Archived',
      archivedDate: '2024-09-18',
      archivedBy: 'Thomas Wilson',
      thumbnailUrl: 'https://images.unsplash.com/photo-1582750433449-648ed127bb54?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: 55,
      durationDisplay: '0:55'
    },
    {
      id: 'KV019',
      kvId: 'ORI-WL-001',
      brandId: 'BRD003',
      categoryId: 'CAT006',
      title: 'Orix Wellness Home Harmony Collection',
      fileName: 'orix_home_harmony_wellness.jpg',
      fileType: 'image',
      fileSize: '3.8MB',
      dimensions: '18x11 ft',
      brandingType: 'Gondola Branding',
      uploadDate: '2024-03-25',
      usageCount: 4,
      usedIn: ['Gondola - Wellness Aisle 1', 'Gondola - Wellness Aisle 2', 'FSU - Health Section 1', 'FSU - Health Section 2'],
      kvStatus: 'Archived',
      archivedDate: '2024-09-08',
      archivedBy: 'Nancy Brown',
      thumbnailUrl: 'https://images.unsplash.com/photo-1574263867128-e8fb1b7506d3?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: null,
      durationDisplay: null
    },
    {
      id: 'KV020',
      kvId: 'SPK-SC-001',
      brandId: 'BRD005',
      categoryId: 'CAT002',
      title: 'Sparkle Youth Skincare Fresh Start Campaign',
      fileName: 'sparkle_youth_fresh_start.jpg',
      fileType: 'image',
      fileSize: '2.1MB',
      dimensions: '11x7 ft',
      brandingType: 'Window Display',
      uploadDate: '2024-07-08',
      usageCount: 6,
      usedIn: ['Window Display - Youth Store 1', 'Window Display - Youth Store 2', 'Window Display - College Store', 'FSU - Student Area 1', 'FSU - Student Area 2', 'In-Store Static - Youth Section'],
      kvStatus: 'Archived',
      archivedDate: '2024-09-22',
      archivedBy: 'Kevin Martinez',
      thumbnailUrl: 'https://images.unsplash.com/photo-1516975080664-ed2fc6a32937?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: null,
      durationDisplay: null
    },
    {
      id: 'KV021',
      kvId: 'ELT-HC-001',
      brandId: 'BRD006',
      categoryId: 'CAT003',
      title: 'Elite Hair Care Salon Professional - Former',
      fileName: 'elite_salon_professional_former.mp4',
      fileType: 'video',
      fileSize: '13.7MB',
      dimensions: '15x9 ft',
      brandingType: 'LED Screen',
      uploadDate: '2024-06-05',
      usageCount: 3,
      usedIn: ['LED Screen - Elite Salon 1', 'LED Screen - Elite Salon 2', 'In-Store Static - Professional Section'],
      kvStatus: 'Archived',
      archivedDate: '2024-09-29',
      archivedBy: 'Patricia Davis',
      thumbnailUrl: 'https://images.unsplash.com/photo-1522337360788-8b13dee7a37e?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: 20,
      durationDisplay: '0:20'
    },
    {
      id: 'KV022',
      kvId: 'VIV-HFC-001',
      brandId: 'BRD004',
      categoryId: 'CAT004',
      title: 'Viva Home Care Eco-Friendly Line - Previous',
      fileName: 'viva_eco_friendly_previous.jpg',
      fileType: 'image',
      fileSize: '2.6MB',
      dimensions: '13x9 ft',
      brandingType: 'FSU',
      uploadDate: '2024-04-18',
      usageCount: 3,
      usedIn: ['FSU - Eco Section 1', 'FSU - Eco Section 2', 'Gondola - Green Products'],
      kvStatus: 'Archived',
      archivedDate: '2024-09-14',
      archivedBy: 'Christopher Taylor',
      thumbnailUrl: 'https://images.unsplash.com/photo-1558618666-fbd3c8536424?w=400&h=300&fit=crop&crop=faces',
      durationSeconds: null,
      durationDisplay: null
    }
  ];

  useEffect(() => {
    // Simulate loading data
    const loadData = async () => {
      setIsLoading(true);
      await new Promise(resolve => setTimeout(resolve, 1000));
      setBrands(mockBrands);
      setCategories(mockCategories);
      setKeyVisuals(mockKeyVisuals);
      
      // Load reserved KV IDs from localStorage
      const savedReservedIds = localStorage.getItem('reservedKvIds');
      if (savedReservedIds) {
        setReservedKvIds(new Set(JSON.parse(savedReservedIds)));
      }
      
      setIsLoading(false);
    };

    loadData();
  }, []);

  // Filter visuals by status and search - EXCLUDE deleted KVs from Archive view
  const activeVisuals = keyVisuals?.filter(visual => visual?.kvStatus === 'Active');
  const archivedVisuals = keyVisuals?.filter(visual => visual?.kvStatus === 'Archived'); // Only show archived, not deleted
  
  const currentVisuals = currentTab === 'active' ? activeVisuals : archivedVisuals;
  
  const filteredVisuals = currentVisuals?.filter(visual => {
    const matchesSearch = !searchQuery || 
      visual?.kvId?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      visual?.title?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      brands?.find(b => b?.id === visual?.brandId)?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
      categories?.find(c => c?.id === visual?.categoryId)?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase());
    
    return matchesSearch;
  });

  // Enhanced Generate next KV ID - checks reserved IDs to prevent reuse
  const generateKvId = (brandCode, categoryCode) => {
    let baseId = `${brandCode}-${categoryCode}-`;
    let nextNumber = 1;
    let candidateId;
    
    // Keep incrementing until we find an ID that's not reserved or in use
    do {
      candidateId = `${baseId}${String(nextNumber)?.padStart(3, '0')}`;
      nextNumber++;
    } while (
      reservedKvIds?.has(candidateId) || 
      keyVisuals?.some(kv => kv?.kvId === candidateId)
    );
    
    return candidateId;
  };

  // Updated archiving logic - check for Active status requirement and dependencies
  const handleArchiveVisual = async (visual) => {
    if (visual?.kvStatus !== 'Active') {
      alert('Only Active KVs can be archived.');
      return;
    }

    // Check for dependencies (mock data - in real app this would be a database call)
    const mockBrandingItems = [
      {
        id: 1,
        brandingId: 'BB-DHA-LIS-001',
        storeName: 'Dhaka Gulshan Store',
        storeCode: 'DHA',
        brandingMedium: 'Billboard',
        location: 'Gulshan-2, Dhaka',
        assignedTo: 'Store Manager',
        currentKVId: visual?.kvId
      },
      {
        id: 2,
        brandingId: 'IS-CTG-CAV-002',
        storeName: 'Chittagong Agrabad Store',
        storeCode: 'CTG', 
        brandingMedium: 'In-Store Static',
        location: 'Agrabad, Chittagong',
        assignedTo: 'Regional Manager',
        currentKVId: visual?.kvId
      },
      {
        id: 3,
        brandingId: 'WD-DHA-COC-003',
        storeName: 'Dhaka Dhanmondi Store',
        storeCode: 'DHA',
        brandingMedium: 'Window Display', 
        location: 'Dhanmondi-15, Dhaka',
        assignedTo: 'Store Manager',
        currentKVId: visual?.kvId
      }
    ];

    // Simulate dependency check - filter items that use this KV
    const dependentItems = mockBrandingItems?.filter(item => 
      item?.currentKVId === visual?.kvId || 
      (visual?.usageCount > 0) // If KV has usage count, it has dependencies
    );

    if (dependentItems?.length === 0) {
      // No dependencies - proceed with direct archiving
      if (window.confirm(`Are you sure you want to archive "${visual?.title}"? It will be moved to the Archive section.`)) {
        setKeyVisuals(prev => prev?.map(kv => 
          kv?.id === visual?.id 
            ? { 
                ...kv, 
                kvStatus: 'Archived',
                archivedDate: new Date()?.toISOString()?.split('T')?.[0],
                archivedBy: 'Current User' // In real app, get from auth
              }
            : kv
        ));
        
        // Show success message
        alert(`KV "${visual?.kvId}" has been archived successfully. No dependencies found.`);
      }
    } else {
      // Dependencies found - open replacement modal
      setDependencyCheckResult({
        hasDependencies: true,
        dependentItems: dependentItems,
        totalDependencies: dependentItems?.length
      });
      setSelectedVisualForReplacement(visual);
      setReplacementModalOpen(true);
    }
  };

  // NEW: Free deletion logic for Archived KVs (Admin only, no dependency checks)
  const handleDeleteVisual = async (visual) => {
    // 1. Admin permission check
    if (currentUserRole !== 'admin') {
      alert('Only Admin users can permanently delete KVs from the archive.');
      return;
    }

    // 2. Archive requirement check
    if (visual?.kvStatus !== 'Archived') {
      alert('KVs can only be deleted if their status is Archived. Active KVs must go through Replace & Archive first.');
      return;
    }

    setSelectedVisualForDeletion(visual);
    setDeletionModalOpen(true);
  };

  // NEW: Confirm permanent deletion with KV ID reservation
  const confirmDeletion = () => {
    if (!selectedVisualForDeletion) return;

    const deletedKvId = selectedVisualForDeletion?.kvId;
    const deletionDate = new Date()?.toISOString()?.split('T')?.[0];
    const deletedBy = 'Current User'; // In real app, get from auth

    // 1. Remove from visuals list (no longer appears in Archive section)
    setKeyVisuals(prev => prev?.filter(kv => kv?.id !== selectedVisualForDeletion?.id));

    // 2. Reserve KV ID permanently to prevent reuse
    const updatedReservedIds = new Set([...reservedKvIds, deletedKvId]);
    setReservedKvIds(updatedReservedIds);
    localStorage.setItem('reservedKvIds', JSON.stringify([...updatedReservedIds]));

    // 3. Store audit log for deleted KV (for audit trail)
    const auditLog = JSON.parse(localStorage.getItem('deletedKvAuditLog') || '[]');
    auditLog?.push({
      kvId: deletedKvId,
      title: selectedVisualForDeletion?.title,
      brand: brands?.find(b => b?.id === selectedVisualForDeletion?.brandId)?.name || 'Unknown Brand',
      category: categories?.find(c => c?.id === selectedVisualForDeletion?.categoryId)?.name || 'Unknown Category',
      fileType: selectedVisualForDeletion?.fileType,
      fileSize: selectedVisualForDeletion?.fileSize,
      dimensions: selectedVisualForDeletion?.dimensions,
      originalUploadDate: selectedVisualForDeletion?.uploadDate,
      archivedDate: selectedVisualForDeletion?.archivedDate,
      archivedBy: selectedVisualForDeletion?.archivedBy,
      deletedDate: deletionDate,
      deletedBy: deletedBy
    });
    localStorage.setItem('deletedKvAuditLog', JSON.stringify(auditLog));

    // 4. Show confirmation and close modal
    alert(`KV "${deletedKvId}" has been permanently deleted. The KV ID is reserved and will never be reused. Deletion has been logged for audit purposes.`);
    
    setDeletionModalOpen(false);
    setSelectedVisualForDeletion(null);
  };

  // Handle KV replacement confirmation with enhanced archiving
  const handleReplacementConfirm = (replacementData) => {
    const { selectedKV, replacementKVs, replacementSummary, installationEntries, auditInfo } = replacementData;
    
    // 1. Archive the original KV after replacements are confirmed
    setKeyVisuals(prev => prev?.map(kv => 
      kv?.id === selectedKV?.id 
        ? { 
            ...kv, 
            kvStatus: 'Archived',
            archivedDate: new Date()?.toISOString()?.split('T')?.[0],
            archivedBy: auditInfo?.triggerUser || 'Current User',
            replacementInfo: {
              wasReplaced: true,
              replacementKVIds: replacementKVs?.map(r => r?.kvId),
              replacementDate: auditInfo?.triggerDate,
              replacementBy: auditInfo?.triggerUser,
              affectedBrandingItems: auditInfo?.affectedBrandingItems
            }
          }
        : kv
    ));

    // 2. Store installation entries for Installation Tracking integration
    const existingInstallations = JSON.parse(localStorage.getItem('installationEntries') || '[]');
    const updatedInstallations = [...existingInstallations, ...installationEntries];
    localStorage.setItem('installationEntries', JSON.stringify(updatedInstallations));
    
    // 3. Store audit trail for KV replacement
    const auditLog = JSON.parse(localStorage.getItem('kvReplacementAuditLog') || '[]');
    auditLog?.push({
      originalKVId: selectedKV?.kvId,
      originalKVTitle: selectedKV?.title,
      replacementKVs: replacementKVs?.map(kv => ({
        kvId: kv?.kvId,
        title: kv?.title
      })),
      affectedBrandingItems: replacementSummary?.length,
      triggerUser: auditInfo?.triggerUser,
      triggerDate: auditInfo?.triggerDate,
      archivedDate: new Date()?.toISOString()?.split('T')?.[0],
      installationEntriesCreated: installationEntries?.length
    });
    localStorage.setItem('kvReplacementAuditLog', JSON.stringify(auditLog));
    
    // 4. Show success message
    alert(`KV "${selectedKV?.kvId}" has been successfully archived with replacements! ${replacementSummary?.length} installation tracking entries created. Navigating to Installation Tracking...`);
    
    // 5. Navigate to installation tracking after a short delay
    setTimeout(() => {
      navigate('/installation-tracking');
    }, 1500);
    
    // 6. Close modals and reset state
    setReplacementModalOpen(false);
    setSelectedVisualForReplacement(null);
    setDependencyCheckResult(null);
  };

  // Handle brand operations
  const handleAddBrand = () => {
    setBrandFormMode('create');
    setEditingBrand(null);
    setIsBrandModalOpen(true);
  };

  const handleEditBrand = (brand) => {
    setBrandFormMode('edit');
    setEditingBrand(brand);
    setIsBrandModalOpen(true);
  };

  const handleBrandSubmit = async (brandData) => {
    if (brandFormMode === 'create') {
      const newBrand = {
        ...brandData,
        id: `BRD${String(brands?.length + 1)?.padStart(3, '0')}`,
      };
      setBrands(prev => [...prev, newBrand]);
    } else {
      setBrands(prev => prev?.map(brand => 
        brand?.id === editingBrand?.id 
          ? { ...brand, ...brandData }
          : brand
      ));
    }
  };

  const handleDeleteBrand = (brand) => {
    if (window.confirm(`Are you sure you want to delete "${brand?.name}"? This will also remove all associated key visuals.`)) {
      setBrands(prev => prev?.filter(b => b?.id !== brand?.id));
      setKeyVisuals(prev => prev?.filter(kv => kv?.brandId !== brand?.id));
    }
  };

  // Handle category operations
  const handleAddCategory = () => {
    setCategoryFormMode('create');
    setEditingCategory(null);
    setIsCategoryModalOpen(true);
  };

  const handleEditCategory = (category) => {
    setCategoryFormMode('edit');
    setEditingCategory(category);
    setIsCategoryModalOpen(true);
  };

  const handleCategorySubmit = async (categoryData) => {
    if (categoryFormMode === 'create') {
      const newCategory = {
        ...categoryData,
        id: `CAT${String(categories?.length + 1)?.padStart(3, '0')}`,
      };
      setCategories(prev => [...prev, newCategory]);
    } else {
      setCategories(prev => prev?.map(category => 
        category?.id === editingCategory?.id 
          ? { ...category, ...categoryData }
          : category
      ));
    }
  };

  const handleDeleteCategory = (category) => {
    if (window.confirm(`Are you sure you want to delete "${category?.name}"? This will also remove all associated key visuals.`)) {
      setCategories(prev => prev?.filter(c => c?.id !== category?.id));
      setKeyVisuals(prev => prev?.filter(kv => kv?.categoryId !== category?.id));
    }
  };

  // Enhanced visual upload with KV ID reservation check
  const handleVisualUpload = async (uploadData) => {
    const brand = brands?.find(b => b?.id === uploadData?.brandId);
    const category = categories?.find(c => c?.id === uploadData?.categoryId);
    
    if (!brand || !category) return;

    const kvId = generateKvId(brand?.code, category?.code);
    
    // Double-check that the generated ID is not reserved
    if (reservedKvIds?.has(kvId)) {
      alert('Error: Generated KV ID conflicts with a reserved ID. Please try again.');
      return;
    }

    const newVisual = {
      id: `KV${String(keyVisuals?.length + 1)?.padStart(3, '0')}`,
      kvId,
      brandId: uploadData?.brandId,
      categoryId: uploadData?.categoryId,
      title: uploadData?.title,
      fileName: uploadData?.file?.name,
      fileType: uploadData?.fileType || (uploadData?.file?.type?.startsWith('image') ? 'image' : 'video'),
      fileSize: uploadData?.fileSizeFormatted || `${(uploadData?.file?.size / (1024 * 1024))?.toFixed(2)} MB`,
      fileSizeFormatted: uploadData?.fileSizeFormatted,
      fileExtension: uploadData?.fileExtension,
      widthFt: uploadData?.widthFt,
      heightFt: uploadData?.heightFt,
      areaSqft: uploadData?.areaSqft,
      dimensions: uploadData?.widthFt && uploadData?.heightFt ? `${uploadData?.widthFt}x${uploadData?.heightFt} ft` : '',
      brandingType: uploadData?.brandingType,
      uploadDate: new Date()?.toISOString()?.split('T')?.[0],
      usageCount: 0,
      usedIn: [],
      // Enhanced metadata fields
      metaWidthPx: uploadData?.metaWidthPx,
      metaHeightPx: uploadData?.metaHeightPx,
      metaAspect: uploadData?.metaAspect,
      enteredAspect: uploadData?.enteredAspect,
      durationSeconds: uploadData?.durationSeconds,
      durationDisplay: uploadData?.durationDisplay,
      thumbnailUrl: uploadData?.thumbnailUrl,
      // Archive functionality fields
      kvStatus: 'Active', // Default status for new KVs
      archivedDate: null,
      archivedBy: null
    };

    setKeyVisuals(prev => [...prev, newVisual]);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading key visuals...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-6">
        <Breadcrumbs />

        {/* Page Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Key Visual Management</h1>
            <p className="text-muted-foreground mt-1">
              Manage brand assets, categories, and visual content for retail branding
            </p>
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
              <span>Role: <span className="font-medium capitalize">{currentUserRole}</span></span>
              <span>•</span>
              <span>Current View: <span className="font-medium capitalize">{currentTab}</span> KVs</span>
              {reservedKvIds?.size > 0 && (
                <>
                  <span>•</span>
                  <span>Reserved IDs: <span className="font-medium text-yellow-600">{reservedKvIds?.size}</span></span>
                </>
              )}
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* New Audit Log Button */}
            <Button
              variant="outline"
              iconName="FileText"
              iconPosition="left"
              onClick={() => {
                const auditLog = JSON.parse(localStorage.getItem('deletedKvAuditLog') || '[]');
                if (auditLog?.length === 0) {
                  alert('No deleted KVs found in audit log.');
                  return;
                }
                
                const csvContent = auditLog?.map(log => 
                  `${log?.kvId},${log?.title},${log?.brand},${log?.category},${log?.fileType},${log?.fileSize || 'N/A'},${log?.dimensions || 'N/A'},${log?.originalUploadDate},${log?.archivedDate},${log?.archivedBy},${log?.deletedDate},${log?.deletedBy}`
                )?.join('\n');
                const headers = 'KV ID,Title,Brand,Category,File Type,File Size,Dimensions,Upload Date,Archived Date,Archived By,Deleted Date,Deleted By';
                const blob = new Blob([`${headers}\n${csvContent}`], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `deleted_kvs_audit_log.csv`;
                a?.click();
                URL.revokeObjectURL(url);
              }}
              className="text-muted-foreground"
            >
              Audit Log ({JSON.parse(localStorage.getItem('deletedKvAuditLog') || '[]')?.length})
            </Button>
            
            <Button
              variant="outline"
              iconName="Download"
              iconPosition="left"
              onClick={() => {
                const csvContent = currentVisuals?.map(kv => 
                  `${kv?.kvId},${kv?.title},${brands?.find(b => b?.id === kv?.brandId)?.name},${categories?.find(c => c?.id === kv?.categoryId)?.name},${kv?.brandingType},${kv?.dimensions},${kv?.uploadDate},${kv?.kvStatus}${kv?.archivedDate ? `,${kv?.archivedDate},${kv?.archivedBy}` : ''}`
                )?.join('\n');
                const headers = currentTab === 'archived' ?'KV ID,Title,Brand,Category,Type,Dimensions,Upload Date,Status,Archived Date,Archived By' :'KV ID,Title,Brand,Category,Type,Dimensions,Upload Date,Status';
                const blob = new Blob([`${headers}\n${csvContent}`], { type: 'text/csv' });
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `key_visuals_${currentTab}_export.csv`;
                a?.click();
              }}
            >
              Export {currentTab?.charAt(0)?.toUpperCase() + currentTab?.slice(1)}
            </Button>
            <Button
              variant="default"
              iconName="Upload"
              iconPosition="left"
              onClick={() => setIsUploadModalOpen(true)}
            >
              Upload Visual
            </Button>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="bg-card rounded-lg border border-border shadow-sm mb-6">
          <div className="p-4">
            <div className="flex items-center gap-1">
              <Button
                variant={currentTab === 'active' ? 'default' : 'ghost'}
                onClick={() => setCurrentTab('active')}
                iconName="Eye"
                iconPosition="left"
                className={`flex items-center gap-2 ${currentTab === 'active' ? '' : 'text-muted-foreground'}`}
              >
                Active ({activeVisuals?.length})
              </Button>
              <Button
                variant={currentTab === 'archived' ? 'default' : 'ghost'}
                onClick={() => setCurrentTab('archived')}
                iconName="Archive"
                iconPosition="left"
                className={`flex items-center gap-2 ${currentTab === 'archived' ? '' : 'text-muted-foreground'}`}
              >
                Archived ({archivedVisuals?.length})
              </Button>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <div className="bg-card p-4 rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Image" size={20} className="text-primary" />
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground">{currentVisuals?.length}</div>
                <div className="text-sm text-muted-foreground">
                  {currentTab === 'active' ? 'Active Visuals' : 'Archived Visuals'}
                </div>
              </div>
            </div>
          </div>
          
          <div className="bg-card p-4 rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center">
                <Icon name="Tag" size={20} className="text-accent" />
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground">{brands?.length}</div>
                <div className="text-sm text-muted-foreground">Active Brands</div>
              </div>
            </div>
          </div>
          
          <div className="bg-card p-4 rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-secondary/10 rounded-lg flex items-center justify-center">
                <Icon name="Folder" size={20} className="text-secondary" />
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground">{categories?.length}</div>
                <div className="text-sm text-muted-foreground">Categories</div>
              </div>
            </div>
          </div>
          
          <div className="bg-card p-4 rounded-lg border border-border">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-success/10 rounded-lg flex items-center justify-center">
                <Icon name="Activity" size={20} className="text-success" />
              </div>
              <div>
                <div className="text-2xl font-bold text-foreground">
                  {currentVisuals?.reduce((sum, kv) => sum + kv?.usageCount, 0)}
                </div>
                <div className="text-sm text-muted-foreground">Total Usage</div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content - Dual Panel Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left Panel - Brand & Category Management */}
          <div className="lg:col-span-4">
            <BrandCategoryPanel
              brands={brands}
              categories={categories}
              onAddBrand={handleAddBrand}
              onEditBrand={handleEditBrand}
              onDeleteBrand={handleDeleteBrand}
              onAddCategory={handleAddCategory}
              onEditCategory={handleEditCategory}
              onDeleteCategory={handleDeleteCategory}
            />
          </div>

          {/* Right Panel - Visual Repository */}
          <div className="lg:col-span-8">
            <VisualRepository
              visuals={filteredVisuals}
              brands={brands}
              categories={categories}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              onDeleteVisual={currentTab === 'active' ? handleArchiveVisual : handleDeleteVisual}
              onUploadClick={() => setIsUploadModalOpen(true)}
              onViewDetail={(visual) => {
                console.log('View detail for:', visual);
              }}
              currentTab={currentTab}
              currentUserRole={currentUserRole}
            />
          </div>
        </div>

        {/* Modals */}
        <UploadModal
          isOpen={isUploadModalOpen}
          onClose={() => setIsUploadModalOpen(false)}
          onSubmit={handleVisualUpload}
          brands={brands}
          categories={categories}
          generateKvId={generateKvId}
        />

        <BrandFormModal
          isOpen={isBrandModalOpen}
          onClose={() => setIsBrandModalOpen(false)}
          onSubmit={handleBrandSubmit}
          brand={editingBrand}
          mode={brandFormMode}
          existingBrands={brands}
        />

        <CategoryFormModal
          isOpen={isCategoryModalOpen}
          onClose={() => setIsCategoryModalOpen(false)}
          onSubmit={handleCategorySubmit}
          category={editingCategory}
          mode={categoryFormMode}
          existingCategories={categories}
        />

        {/* KV Replacement Modal */}
        <KVReplacementModal
          isOpen={replacementModalOpen}
          onClose={() => {
            setReplacementModalOpen(false);
            setSelectedVisualForReplacement(null);
            setDeletionModalOpen(false);
            setSelectedVisualForDeletion(null);
            setDependencyCheckResult(null);
          }}
          selectedKV={selectedVisualForReplacement}
          keyVisuals={keyVisuals}
          brands={brands}
          categories={categories}
          dependencyCheckResult={dependencyCheckResult}
          onReplacementConfirm={handleReplacementConfirm}
        />

        {/* UPDATED: Simplified Deletion Confirmation Modal for Archived KVs */}
        {deletionModalOpen && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-card rounded-lg border border-border shadow-elevated w-full max-w-md">
              <div className="p-6 border-b border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-destructive/10 rounded-lg flex items-center justify-center">
                    <Icon name="AlertTriangle" size={20} className="text-destructive" />
                  </div>
                  <div>
                    <h2 className="text-xl font-semibold text-foreground">Permanent Deletion</h2>
                    <p className="text-sm text-muted-foreground">This action cannot be undone</p>
                  </div>
                </div>
              </div>

              <div className="p-6 space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <h3 className="font-medium text-red-800 mb-2 flex items-center gap-2">
                    <Icon name="Shield" size={16} />
                    Deletion Confirmation
                  </h3>
                  <div className="space-y-2 text-sm text-red-700">
                    <div><span className="font-medium">KV ID:</span> {selectedVisualForDeletion?.kvId}</div>
                    <div><span className="font-medium">Title:</span> {selectedVisualForDeletion?.title}</div>
                    <div><span className="font-medium">Brand:</span> {brands?.find(b => b?.id === selectedVisualForDeletion?.brandId)?.name}</div>
                    <div><span className="font-medium">Status:</span> {selectedVisualForDeletion?.kvStatus}</div>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
                <Button
                  variant="outline"
                  onClick={() => {
                    setDeletionModalOpen(false);
                    setSelectedVisualForDeletion(null);
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="destructive"
                  onClick={confirmDeletion}
                  iconName="Trash2"
                  iconPosition="left"
                >
                  Delete Permanently
                </Button>
              </div>
            </div>
          </div>
        )}

        <QuickActionButton />
      </main>
    </div>
  );
};

export default KeyVisualManagement;